import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Palette, Box, Flex, Text } from '@updater/ui-uds';
import { isTaskCompletedOrValidatingOrPending } from 'flows/core/utils/card-makers/utils';
import { InitiatePayload } from 'flows/core/types/cards';
import { getDaysText, useDaysLeft } from './utils';
import { ArrowRight } from '@phosphor-icons/react';
import { CompletedSegment, ValidatingSegment } from './progress-bar-styles';

const Card = styled(Box)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 1rem;
  padding: 0.75rem 1.25rem;
  min-height: 6.125rem;
  box-shadow: ${({ theme }) => theme.shadows.shadow10};
`;

const BarContainer = styled(Box)`
  width: 100%;
  height: 0.5rem;
  background-color: ${Palette.mint100}; /* no theme.colors equivalent for mint10 */
  border-radius: 0.25rem;
  margin: 0.5rem 0;
`;

export interface ProgressBarCardProps {
  priorityCards: InitiatePayload[];
  completedCards: InitiatePayload[];
  validatingCards: InitiatePayload[];
  currentMoveDate: string | null | undefined;
  onStartTask?: () => void;
}

export const ProgressBarCard: FC<ProgressBarCardProps> = ({
  priorityCards,
  completedCards,
  validatingCards,
  currentMoveDate,
  onStartTask,
}) => {
  const theme = useTheme();
  const daysLeft = useDaysLeft(currentMoveDate);

  const completedRequiredTasks = useMemo(() => {
    if (!completedCards) return [];
    return completedCards.filter(
      (card: InitiatePayload) =>
        isTaskCompletedOrValidatingOrPending(card.data) &&
        (card.data?.required || card.data?.priority)
    );
  }, [completedCards]);

  const totalTasks =
    (priorityCards?.length || 0) + completedRequiredTasks.length;
  const validatingTasks = validatingCards?.length || 0;
  const completedTasks = completedRequiredTasks.length;
  const combinedProgress = validatingTasks + completedTasks;

  const pct =
    totalTasks > 0 ? Math.round((combinedProgress / totalTasks) * 100) : 0;
  const completedPct =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const validatingPct =
    totalTasks > 0 ? Math.round((validatingTasks / totalTasks) * 100) : 0;
  const remainingTasks = totalTasks - combinedProgress;

  let title: string;
  if (pct === 100) title = "You're all set";
  else if (remainingTasks === 1) title = 'Just one more!';
  else if (pct === 50) title = "You're halfway there!";
  else if (pct > 50) title = 'Over halfway there!';
  else if (pct === 0) title = 'Get started';
  else title = 'Moving progress';

  if (totalTasks === 0) {
    return null;
  }

  return (
    <Card data-testid="progress-bar-card">
      <Flex alignItems="center" justifyContent="space-between">
        {pct === 0 ? (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            onClick={() => onStartTask?.()}
            style={{ cursor: 'pointer' }}
          >
            <Text variant="lBold" style={{ color: theme.colors.cta }}>
              {title}
            </Text>
            <ArrowRight size={24} color={theme.colors.cta} />
          </Flex>
        ) : (
          <>
            <Text variant="lBold">{title}</Text>
            <Text variant="lBold">{pct}%</Text>
          </>
        )}
      </Flex>

      <BarContainer>
        <Flex width="100%" height="100%">
          {completedPct > 0 && (
            <CompletedSegment
              widthPct={completedPct}
              hasValidating={validatingPct > 0}
              data-tooltip="Completed tasks"
            />
          )}
          {validatingPct > 0 && (
            <ValidatingSegment
              widthPct={validatingPct}
              hasCompleted={completedPct > 0}
              data-tooltip="Tasks in review"
            />
          )}
        </Flex>
      </BarContainer>

      <Flex justifyContent="space-between" flexWrap="wrap" gap="0.25rem">
        {pct === 0 ? (
          <Text variant="s">{getDaysText(daysLeft)}</Text>
        ) : (
          <>
            <Text variant="s">
              {completedTasks > 0 && `● ${completedTasks} done`}{' '}
              {validatingTasks > 0 && `● ${validatingTasks} in review`}{' '}
              {(() => {
                const remaining = totalTasks - validatingTasks - completedTasks;
                if (remaining <= 0) return null;
                return `● ${remaining} priority task${
                  remaining === 1 ? '' : 's'
                } remaining`;
              })()}
            </Text>
            <Text variant="s">{getDaysText(daysLeft)}</Text>
          </>
        )}
      </Flex>
    </Card>
  );
};
